import type { ComponentProps, CSSProperties, ReactNode, Ref } from 'react'
import { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import clsx from 'clsx'
import SearchForm from 'components/Search/SearchForm'
import type { SearchFormRef } from 'components/Search/SearchForm'
import LayoutContainer from 'components/Common/LayoutContainer'
import Pane from 'components/Common/Pane'
import { GlobalSearchDispatchContext, GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import useScrollDirection from 'hooks/useScrollDirection'
import { useInView } from 'react-intersection-observer'
import { mediaQueryOnly, mediaQueryUp } from 'components/utils/breakpoint'
import HotelSearchPageFilters from './HotelSearchPageFilters'
import type { OfferListSortOption } from 'constants/offerListFilters'
import zIndex from 'styles/tools/z-index'
import { ANYWHERE_PLACE_ID, HOTEL_SEARCH_TYPEAHEAD_TYPES } from 'constants/search'
import useLocationSearchLabels from 'hooks/Search/useLocationSearchLabels'
import useGlobalSearchURLHashVertical from 'hooks/GlobalSearch/useGlobalSearchURLHashVertical'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import { useIsMobileOrTablet } from 'hooks/useIsMobileOrTablet'
import { connect } from 'react-redux'
import AnywhereFilterTabs from './AnywhereFilterTabs'
import { parseSearchString } from 'lib/url/searchUrlUtils'
import { SearchMenuStates } from 'components/Search/type'
import type { EnabledFilters } from 'components/Search/type'
import HotelExposedFiltersHeader from './HotelExposedFiltersHeader/HotelExposedFiltersHeader'
import SearchField from './SearchField'
import TextButton from 'LuxKit/Button/TextButton'
import LineArrowLeftIcon from 'LuxKit/Icons/line/LineArrowLeftIcon'
import { useAppDispatch } from 'hooks/reduxHooks'
import { useAppNavigation } from 'lib/routing/useAppNavigation'
import { pushWithRegion } from 'actions/NavigationActions'

const SearchFormContainer = styled(LayoutContainer)`
  padding: ${rem(8)};
  display: none;
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};

  ${mediaQueryUp.tablet} {
    display: block;
  }

  &.map-view {
    ${mediaQueryOnly.tablet} {
      display: none;
    }
  }

  ${mediaQueryUp.desktop} {
    padding: ${rem(16)} ${rem(32)};
  }

  &&.map-redesign {
    max-width: none;
    position: relative;
    padding-inline: ${rem(144)} ${rem(12)};
  }

  &.edit-mode {
    display: block;
  }

  &.nav-redesign:not(.edit-mode) {
    ${mediaQueryUp.tablet} {
      display: none;
    }
  }
`

const MapListViewButton = styled(TextButton)`
  position: absolute;
  inset-block-start: 50%;
  inset-inline-start: ${rem(12)};
  transform: translateY(-50%);
`

const Wrapper = styled.div`
  border-top: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  margin-top: ${rem(8)};
`

const TabsContainer = styled(LayoutContainer)`
  padding-block: ${rem(8)};
  padding-inline: ${rem(16)} 0;
  border-top: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  ${mediaQueryUp.tablet} {
    border: none;
    padding-top: ${rem(16)};
    padding-bottom: ${rem(16)};
  }

  &.no-divider {
    border-top: none;
    padding-block: 0 ${rem(12)};

    ${mediaQueryUp.tablet} {
      max-width: none;
      margin-inline: 0;
      padding-inline-start: ${rem(20)};
    }

    ${mediaQueryUp.desktop} {
      padding-inline-start: ${rem(32)};
    }
  }
`

const StyledPane = styled(Pane)`
  position: sticky;
  transition: top 1s ease-in-out;
  z-index: ${zIndex.searchMenu};

  &.disableSticky {
    position: relative;
    top: auto;
    transition: none;
    box-shadow: none;
  }

  &.alwaysSticky {
    top: 0;
    z-index: ${zIndex.stickySubHeader};
    transition: none;
  }

  &.offScreen:not(.alwaysSticky):not(.disableSticky) {
    width: 100%;
    top: -50%;
    &:not(.mapPage) {
      box-shadow: ${props => props.theme.shadow.bottom.small};
    }
    transition: top 1s ease-in-out;
  }
  &.lifted:not(.alwaysSticky):not(.disableSticky) {
    top: 0;
    transition: top 0.5s ease-out;
    z-index: ${zIndex.stickySubHeader};
    &:not(.mapPage) {
      box-shadow: ${props => props.theme.shadow.bottom.small};
    }
  }
`

const FilterContainer = styled.div`
  padding: ${rem(8)} ${rem(16)};
  border-top: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-bottom: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
`

const SENTINEL_STYLE: CSSProperties = { display: 'none' }

interface MappedStateProps {
  windowSearch: string
  pathname: string
}

type InheritedSearchFieldProps = Pick<ComponentProps<typeof SearchField>, 'onSortChange' | 'onFilterChange'>

interface Props extends InheritedSearchFieldProps {
  mapRedesign?: boolean
  filters: App.OfferListFilters
  formRef: Ref<SearchFormRef>
  offerListFiltersProps: {
    searchType: App.SearchItem['searchType']
    switchView: {
      label: string
      view: 'map' | 'list'
      URL: string
      goBack?: boolean
    }
    sortOptions?: Array<OfferListSortOption>
    listFilters: App.OfferListFilters
    enabledFiltersOverrides?: Partial<EnabledFilters>
  }
  searchVertical: App.SearchVertical
  baseSearchPath: string
  baseSearchMapPath: string
  disableAnywhereFilterTabs?: boolean
  locationSearchPlaceholder?: string
  typeaheadTypes?: Array<App.SearchPlaceType>
  showFilters?: boolean
  useMapLayout?: boolean
  mapLayoutFilterRow?: ReactNode
  alwaysSticky?: boolean
  /** Opt out of sticky / scroll-reappear so a parent can own that behaviour. */
  disableSticky?: boolean
  flat?: boolean
  compactSearchField?: (openSearchMenu: () => void) => ReactNode
  onPaneHeightChange?: (height: number) => void
}

function HotelSearchHeader(props: Props & MappedStateProps) {
  const dispatch = useAppDispatch()
  const navigation = useAppNavigation()
  const locationLabels = useLocationSearchLabels()
  const {
    formRef,
    mapRedesign = false,
    filters,
    offerListFiltersProps: { searchType, switchView, sortOptions, listFilters, enabledFiltersOverrides },
    windowSearch,
    searchVertical,
    baseSearchPath,
    baseSearchMapPath,
    disableAnywhereFilterTabs,
    showFilters = true,
    locationSearchPlaceholder,
    typeaheadTypes = HOTEL_SEARCH_TYPEAHEAD_TYPES,
    pathname,
    onFilterChange,
    onSortChange,
    useMapLayout = false,
    mapLayoutFilterRow,
    alwaysSticky = false,
    disableSticky = false,
    flat = false,
    compactSearchField,
    onPaneHeightChange,
  } = props

  const {
    isEditMode,
    activeMenu,
  } = useContext(GlobalSearchStateContext)
  const globalSearchDispatch = useContext(GlobalSearchDispatchContext)
  const { toggleSearchVertical } = useGlobalSearchURLHashVertical()

  const openSearchMenu = useCallback(() => { toggleSearchVertical(searchVertical) }, [toggleSearchVertical, searchVertical])
  const backToList = useCallback(() => {
    if (switchView.goBack) {
      navigation.goBack()
      return
    }
    dispatch(pushWithRegion(switchView.URL))
  }, [dispatch, navigation, switchView])

  const scrollDirection = useScrollDirection({ tolerance: 20 })

  const [toLiftRef, liftInView] = useInView()

  const params = useMemo(() => parseSearchString(windowSearch), [windowSearch])

  const isAnywhereSearch = params.destinationId === ANYWHERE_PLACE_ID

  useEffect(() => {
    if (isEditMode) {
      window.scrollTo(0, 0)
    }
  }, [isEditMode])

  useEffect(() => {
    if (!liftInView && isEditMode && scrollDirection === 'down') {
      globalSearchDispatch({
        type: GlobalSearchStateActions.SET_EDIT_MODE,
        isEditMode: false,
      })
    }
  }, [liftInView, isEditMode, scrollDirection, globalSearchDispatch])

  const onSearch = useCallback(() => {
    globalSearchDispatch({
      type: GlobalSearchStateActions.SET_EDIT_MODE,
      isEditMode: false,
    })
  }, [globalSearchDispatch])

  const paneElementRef = useRef<HTMLElement | null>(null)
  const paneSentinelRef = useCallback((node: HTMLDivElement | null) => {
    paneElementRef.current = node?.parentElement ?? null
  }, [])

  useEffect(() => {
    if (!onPaneHeightChange) return
    const pane = paneElementRef.current
    if (!pane) return

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (!entry) return
      const height = entry.borderBoxSize?.[0]?.blockSize ?? entry.target.getBoundingClientRect().height
      onPaneHeightChange(height)
    })
    observer.observe(pane)
    return () => observer.disconnect()
  }, [onPaneHeightChange])

  const isLifted = !liftInView && scrollDirection === 'up'
  const isOffScreen = !liftInView && scrollDirection !== 'up'

  const isMobileScreen = useScreenSizeOnly('mobile')
  const isTabletScreen = useScreenSizeOnly('tablet')
  const isMobileOrTablet = useIsMobileOrTablet()

  // Scoped to hotel search results: H&V and the meta page reuse this header behind the
  // global bar, and meta also reports searchVertical 'hotels' — so exclude it by route.
  const isHotelSearchResults = searchVertical === 'hotels' && !pathname.includes('metasearch')

  const showNavRedesign = isHotelSearchResults && isMobileOrTablet
  // Force sticky so the header pins instead of hiding on scroll-down.
  const alwaysStickyEffective = !disableSticky && (alwaysSticky || showNavRedesign)
  const scrollAwareSticky = !disableSticky && !alwaysStickyEffective

  const isMapPage = pathname.includes('map')
  const isMapLayout = isMapPage || useMapLayout
  // Prefer compactSearchField when provided (meta V2) so SSR doesn't wait on viewport.
  const showCompactSearchField = !!compactSearchField || showNavRedesign || isMobileScreen || (isMapPage && isTabletScreen)
  const isHotelSearch = searchVertical === 'hotels'
  const resolvedLocationSearchPlaceholder = locationSearchPlaceholder ?? (isHotelSearch ? locationLabels.placeholderLabelNaturalLanguage : undefined)
  const resolvedLabel = isHotelSearch ? locationLabels.titleLabelNaturalLanguage : locationLabels.titleLabel

  const searchFormElement = <SearchForm
    submitOnApply
    filters={filters}
    ref={formRef}
    onSubmit={onSearch}
    typeaheadTypes={typeaheadTypes}
    switchView={mapRedesign ? undefined : switchView}
    baseSearchPath={baseSearchPath}
    baseSearchMapPath={baseSearchMapPath}
    locationSearchPlaceholder={resolvedLocationSearchPlaceholder}
    label={resolvedLabel}
    showNaturalLanguageSearchSuggestions={isHotelSearch}
    useMapLayout={useMapLayout}
  />

  const mapLayoutFilters = useMemo(() => {
    if (mapLayoutFilterRow) {
      return <FilterContainer>{mapLayoutFilterRow}</FilterContainer>
    }

    if (showFilters) {
      return (
        <FilterContainer>
          <HotelExposedFiltersHeader filters={listFilters} onFilterChange={onFilterChange} onSortChange={onSortChange} />
        </FilterContainer>
      )
    }

    return null
  }, [listFilters, mapLayoutFilterRow, onFilterChange, onSortChange, showFilters])

  return <>
    <StyledPane
      className={clsx({
        disableSticky,
        alwaysSticky: alwaysStickyEffective,
        lifted: scrollAwareSticky && (isLifted || activeMenu !== SearchMenuStates.Closed),
        offScreen: scrollAwareSticky && isOffScreen && activeMenu === SearchMenuStates.Closed,
        mapPage: isMapPage,
      })}
      shadow={!flat && !isMapLayout && !showNavRedesign ? 'bottom-small' : undefined}
    >
      {onPaneHeightChange && <div ref={paneSentinelRef} style={SENTINEL_STYLE} />}
      {showCompactSearchField && (compactSearchField ?
          compactSearchField(openSearchMenu) :
          <SearchField
            onEdit={openSearchMenu}
            filters={listFilters}
            sortOptions={sortOptions}
            onFilterChange={onFilterChange}
            onSortChange={onSortChange}
            showFilters={isMapLayout ? false : showFilters}
            isMapView={isMapPage}
            navRedesign={showNavRedesign}
            showBrandAndAccount={showNavRedesign && !isMapPage}
            switchView={switchView}
          />)}
      {isMapLayout &&
        <>
          {!showNavRedesign &&
            <SearchFormContainer as="div" className={clsx('map-view', { 'map-redesign': mapRedesign })}>
              {mapRedesign && switchView.view === 'list' && <MapListViewButton
                kind="tertiary"
                size="large"
                onClick={backToList}
                data-testid="list-view"
                startIcon={<LineArrowLeftIcon />}
              >
                {switchView.label}
              </MapListViewButton>}
              {searchFormElement}
            </SearchFormContainer>}
          {!showNavRedesign && !mapRedesign && mapLayoutFilters}
        </>}
      {!isMapLayout &&
        <SearchFormContainer className={clsx({
          'edit-mode': isEditMode && liftInView,
          'nav-redesign': showNavRedesign,
        })}
        >
          {searchFormElement}
        </SearchFormContainer>}
      {!showNavRedesign && !isMobileScreen && isAnywhereSearch && !isMapLayout && !disableAnywhereFilterTabs && <Wrapper>
        <TabsContainer>
          <AnywhereFilterTabs
            filters={listFilters}
            search={windowSearch}
          />
        </TabsContainer>
      </Wrapper>}
      {(mapRedesign || searchType !== 'property') && isMapPage &&
        <HotelSearchPageFilters
          filters={listFilters}
          sortOptions={sortOptions}
          switchView={isMobileScreen ? undefined : switchView}
          enabledFiltersOverrides={enabledFiltersOverrides}
          onFilterChange={onFilterChange}
          onSortChange={onSortChange}
        />}
      {(showNavRedesign || isMobileScreen) && isAnywhereSearch && !isMapPage && !disableAnywhereFilterTabs && <TabsContainer className="no-divider">
        <AnywhereFilterTabs filters={listFilters} search={windowSearch} pills />
      </TabsContainer>}
    </StyledPane>
    <div ref={toLiftRef} />
  </>
}

export default connect<MappedStateProps, undefined, Props, App.State>((appState): MappedStateProps => ({
  windowSearch: appState.router.location.search,
  pathname: appState.router.location.pathname,
}))(HotelSearchHeader)
